import Box, { boxClasses } from '@mui/material/Box';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import { ListItemIcon as ListItemIconDefault, listItemIconClasses } from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import ListItem from '@mui/material/ListItem';
import ListItemButton, { listItemButtonClasses } from '@mui/material/ListItemButton';
import DeleteForever from '@mui/icons-material/DeleteForever';
import Description from '@mui/icons-material/Description';
import Public from '@mui/icons-material/Public';
import FolderIcon from '@mui/icons-material/Folder';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import { styled } from '@mui/material/styles';
import _ from 'lodash';
import { GridEntity } from '../../../query/types';
import Collapse from '@mui/material/Collapse';

import { changePage, loadGrids } from '../../actions'
import { listDeleteAction } from '../../../query/actions'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { conf, RouteConf, ComponentConf } from 'src/config'
import * as routes from '../../routes'
import { Dictionary, isArray } from 'lodash';
import { getPrincipal } from 'src/auth';
import { GetGrids } from '../../selectors';
import LeftDrawer from '../components/LeftDrawer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ViewListIcon from '@mui/icons-material/ViewList';
import { isResourceAllowed } from 'src/auth';
import { getLabel } from './utils'

const ListItemIcon = styled(ListItemIconDefault)(({ theme }) => ({
    [`&.${listItemIconClasses.root}`]: {
        minWidth: 0,
        marginRight: theme.spacing(1),
        marginTop: theme.spacing(0.5)
    }
}));

const BoxSmall = styled(Box)(({ theme }) => ({
    [`&.${boxClasses.root}`]: {
        marginTop: theme.spacing(0.5),
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.palette.grey[400],
        borderRadius: 5,
        height: '1.5em',
        [`:hover`]: {
            backgroundColor: theme.palette.grey[300]
        },
    },
}));

const GridListItemButton = styled(ListItemButton)(({ theme }) => ({
    [`&.${listItemButtonClasses.root}`]: {
        paddingLeft: theme.spacing(4),
    }
}));

const ItemIcon: React.FC<{ rtConf: RouteConf }> = ({ rtConf }) => {
    const compConf: ComponentConf | ComponentConf[] = rtConf.comp;
    let icon = <FolderIcon sx={{ minWidth: undefined }} />

    if (!isArray(compConf)) {
        if (compConf.type === 'entity') {
            icon = <CreateNewFolderIcon />
        }
    }

    return icon;
}

const getListResourceName: (rtConf: RouteConf) => string = (rtConf) => {
    const compConf: ComponentConf | ComponentConf[] = rtConf.comp;
    return isArray(compConf) ? '' : compConf.type == 'list' ? compConf.resourceName : '';
}

const AdminMenu: React.FC = () => {
    const dispatch = useDispatch()
    const rtConf = conf.getUiConf().routes
    const grids = useSelector(GetGrids);
    const [openListMenus, setOpenListMenus] = useState<string[]>([]);

    const paths = useMemo<string[]>(
        () => (
            Object.keys(
                _.pickBy(
                    rtConf,
                    (val, key) => isResourceAllowed(getListResourceName(val))
                )
            )
                .filter(path => path.startsWith('/admin/') && path.indexOf(':') === -1)
        ),
        []
    );

    const listResourceNames = useMemo<string[]>(
        () => (paths.reduce((acc, path, index) => {
            const conf = rtConf[path].comp;
            if (Array.isArray(conf)) {
                conf.reduce((a, c) => {
                    if (c.type == "list") {
                        a.push(c.resourceName);
                    }
                    return a;
                }, acc);
            } else if (conf.type == "list") {
                acc.push(conf.resourceName);
            }
            return acc;
        }, [] as string[])),
        [paths]
    );

    useEffect(() => {
        dispatch(loadGrids(listResourceNames));
    }, [listResourceNames]);

    const handleNavigate = (pathname: string, search?: string) => (e: React.SyntheticEvent) => {
        dispatch(changePage(pathname, search));
    }

    const handleDeleteGrid = (gridId: string) => (e: React.SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(listDeleteAction(gridId));
    }

    const handleListMenuArrowClick = (resource: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const index = openListMenus.indexOf(resource);
        if (index != -1) {
            const newOpenListMenus = [...openListMenus];
            newOpenListMenus.splice(index, 1);
            setOpenListMenus(newOpenListMenus);
        } else {
            setOpenListMenus([...openListMenus, resource]);
        }
    }

    return (
        <LeftDrawer>
            <Box sx={{ overflow: 'auto' }}>
                <List>
                    <ListItemButton onClick={handleNavigate(routes.ROUTEHOME)}>
                        <ListItemIcon>
                            <Public />
                        </ListItemIcon>
                        <ListItemText primary={'Home'} />
                    </ListItemButton>
                    <Divider />
                    <ListItemButton onClick={handleNavigate(routes.ROUTEADMIN + routes.ROUTEOPENAPI)}>
                        <ListItemIcon>
                            <Description />
                        </ListItemIcon>
                        <ListItemText primary={'Openapi Docs'} />
                    </ListItemButton>
                </List>
                <Divider />
                <List>
                    {paths.map((path, index) => {
                        const routeConfig = rtConf[path];
                        const resource = getListResourceName(routeConfig);

                        return <React.Fragment key={index}>
                            <ListItemButton onClick={handleNavigate(path)} alignItems='flex-start'>
                                <ListItemIcon>
                                    <ItemIcon rtConf={routeConfig} />
                                </ListItemIcon>
                                <ListItemText primary={getLabel(routeConfig)} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} />
                                {
                                    grids[resource] != null &&
                                    <BoxSmall onClick={handleListMenuArrowClick(resource)}>
                                        {openListMenus.includes(resource) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </BoxSmall>
                                }
                            </ListItemButton>

                            <Collapse in={openListMenus.includes(resource)}>
                                {
                                    _.keys(grids[resource]).map((gridId, index) => {
                                        const grid: GridEntity = grids[resource][gridId];

                                        return <GridListItemButton key={index} onClick={handleNavigate(path, `gridId=${grid.id!}`)} alignItems='flex-start'>
                                            <ListItemIcon>
                                                <ViewListIcon />
                                            </ListItemIcon>
                                            <ListItemText primary={grid.name} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} />
                                            <BoxSmall onClick={handleDeleteGrid(grid.id!)} sx={{ color: theme => theme.palette.grey[600] }}>
                                                <DeleteForever />
                                            </BoxSmall>
                                        </GridListItemButton>
                                    })
                                }
                            </Collapse>
                        </React.Fragment>
                    })}
                </List>
            </Box>
        </LeftDrawer >
    )
}

export default AdminMenu;
